import type {
  InstallmentFrequency, InstallmentPlan, InstallmentPlanCreate,
  InstallmentPlanStatus, InstallmentScheduleRow, LateFeeKind,
} from '@erp/shared';
import { CalendarDays, Eye } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Badge, Button, Card, Combobox, type ComboboxOption, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, Input, Skeleton,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatMoney, getCurrencyDecimals } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';

const STATUS_TONE: Record<InstallmentPlanStatus, 'info' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  draft: 'neutral',
  active: 'info',
  paid: 'success',
  defaulted: 'danger',
  cancelled: 'neutral',
};

const SCHEDULE_TONE: Record<InstallmentScheduleRow['status'], 'info' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  pending: 'neutral',
  partial: 'warning',
  paid: 'success',
  overdue: 'danger',
  written_off: 'neutral',
};

export function InstallmentPlansPage(): JSX.Element {
  const { t, i18n } = useTranslation('installments');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('installments.write');
  const [items, setItems] = useState<InstallmentPlan[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const h = window.setTimeout(() => setDebounced(query), 200);
    return () => window.clearTimeout(h);
  }, [query]);

  const reload = async (q: string = debounced): Promise<void> => {
    const url = q.trim().length > 0
      ? `/installments/plans?q=${encodeURIComponent(q.trim())}`
      : '/installments/plans';
    const r = await api().get<{ items: InstallmentPlan[] }>(url);
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  if (!items) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const columns: DataTableColumn<InstallmentPlan>[] = [
    {
      id: 'number',
      header: t('plans.columns.number'),
      sortValue: (p) => p.planNumber,
      cellClassName: 'font-mono text-xs',
      cell: (p) => p.planNumber,
    },
    {
      id: 'principal',
      header: t('plans.columns.principal'),
      sortValue: (p) => Number(p.principal),
      cellClassName: 'font-mono text-xs',
      cell: (p) => formatMoney(p.principal, { locale: i18n.language, currency: p.currencyCode }),
    },
    {
      id: 'total',
      header: t('plans.columns.total'),
      sortValue: (p) => Number(p.total),
      cellClassName: 'font-mono text-xs',
      cell: (p) => formatMoney(p.total, { locale: i18n.language, currency: p.currencyCode }),
    },
    {
      id: 'installments',
      header: t('plans.columns.installments'),
      sortValue: (p) => p.installmentCount,
      cellClassName: 'font-mono text-xs',
      cell: (p) => `${p.installmentCount} × ${p.frequency}`,
    },
    {
      id: 'firstDue',
      header: t('plans.columns.firstDue'),
      sortValue: (p) => p.firstDueDate,
      cellClassName: 'font-mono text-xs',
      cell: (p) => p.firstDueDate,
    },
    {
      id: 'status',
      header: t('plans.columns.status'),
      sortValue: (p) => t(`plans.status.${p.status}`),
      cell: (p) => <Badge tone={STATUS_TONE[p.status]}>{t(`plans.status.${p.status}`)}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-32',
      cell: (p) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedId(p.id)}
          title={tc('actions.viewAll')}
        >
          <Eye className="h-4 w-4" aria-hidden />
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('plans.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('plans.description')}</p>
        </div>
        {canWrite && !showCreate && (
          <Button onClick={() => setShowCreate(true)}>{t('plans.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <CreatePlanForm
          onCancel={() => setShowCreate(false)}
          onCreated={async (newId) => {
            setShowCreate(false);
            await reload();
            setSelectedId(newId);
          }}
        />
      )}

      <Input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('plans.searchPlaceholder')}
        aria-label={t('plans.searchPlaceholder')}
      />

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" aria-hidden />}
          title={t('plans.empty')}
          description={t('plans.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('plans.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(p) => p.id}
            searchable={false}
          />
        </Card>
      )}

      {selectedId && (
        <PlanDetailDrawer
          planId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={async () => { await reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface CreateFormProps {
  onCancel: () => void;
  onCreated: (newId: string) => void | Promise<void>;
}

function CreatePlanForm({ onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('installments');
  const { t: tc } = useTranslation();
  const [parties, setParties] = useState<Array<{ id: string; code: string; displayName: string }> | null>(null);
  const [form, setForm] = useState<InstallmentPlanCreate>({
    partyId: '',
    currencyCode: 'USD',
    principal: '0',
    installmentCount: 12,
    frequency: 'monthly' as InstallmentFrequency,
    firstDueDate: new Date().toISOString().slice(0, 10),
    graceDays: 7,
    lateFeeKind: 'none',
  });
  const [showLateFee, setShowLateFee] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // The whole book: a plan is opened for one named customer, and a one-page
    // picker simply could not offer most of them.
    void fetchAllParties<{ id: string; code: string; displayName: string }>()
      .then(setParties)
      .catch(() => undefined);
  }, []);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await api().post<InstallmentPlan>('/installments/plans', form);
      await onCreated(r.data.id);
    } catch (err) {
      setError((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{t('plans.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="p-party" label={t('plans.create.party')} required>
          <Combobox
            id="p-party"
            options={(parties ?? []).map((p): ComboboxOption => ({ value: p.id, label: `${p.code} — ${p.displayName}`, keywords: `${p.code} ${p.displayName}` }))}
            value={form.partyId || undefined}
            onChange={(v) => setForm({ ...form, partyId: v ?? '' })}
            placeholder={t('plans.create.party', { defaultValue: 'Customer' })}
            ariaLabel={t('plans.create.party', { defaultValue: 'Customer' })}
          />
        </FormField>
        <FormField id="p-currency" label={t('plans.create.currency')} required>
          <CurrencySelect id="p-currency" value={form.currencyCode} onChange={(c) => setForm({ ...form, currencyCode: c })} required />
        </FormField>
        <FormField id="p-principal" label={t('plans.create.principal')} required>
          <Input id="p-principal" inputMode="decimal" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} required />
        </FormField>
        <FormField id="p-markup" label={t('plans.create.markupRate')}>
          <Input id="p-markup" inputMode="decimal" value={form.markupRate ?? ''} onChange={(e) => setForm({ ...form, markupRate: e.target.value })} placeholder="0.0500 = 5%" />
        </FormField>
        <FormField id="p-count" label={t('plans.create.count')} required>
          <Input id="p-count" type="number" min={1} max={120} value={form.installmentCount} onChange={(e) => setForm({ ...form, installmentCount: Number(e.target.value) || 1 })} required />
        </FormField>
        <FormField id="p-freq" label={t('plans.create.frequency')} required>
          <select
            id="p-freq"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value as InstallmentFrequency })}
          >
            <option value="monthly">{t('plans.frequency.monthly')}</option>
            <option value="biweekly">{t('plans.frequency.biweekly')}</option>
            <option value="weekly">{t('plans.frequency.weekly')}</option>
          </select>
        </FormField>
        <FormField id="p-firstdue" label={t('plans.create.firstDue')} required>
          <Input id="p-firstdue" type="date" value={form.firstDueDate} onChange={(e) => setForm({ ...form, firstDueDate: e.target.value })} required />
        </FormField>

        <div className="md:col-span-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => setShowLateFee((v) => !v)}
            className="text-sm font-medium text-fg-muted hover:text-fg"
            aria-expanded={showLateFee}
          >
            {showLateFee ? '▾' : '▸'} {t('plans.create.lateFeeSection')}
          </button>
        </div>
        {showLateFee && (
          <>
            <FormField id="p-grace" label={t('plans.create.graceDays')}>
              <Input
                id="p-grace"
                type="number"
                min={0}
                max={180}
                value={form.graceDays ?? 7}
                onChange={(e) => setForm({ ...form, graceDays: Math.max(0, Math.min(180, Number(e.target.value) || 0)) })}
              />
            </FormField>
            <FormField id="p-lf-kind" label={t('plans.create.lateFeeKind')}>
              <select
                id="p-lf-kind"
                className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
                value={form.lateFeeKind ?? 'none'}
                onChange={(e) => setForm({ ...form, lateFeeKind: e.target.value as LateFeeKind })}
              >
                <option value="none">{t('plans.create.lateFeeKindNone')}</option>
                <option value="fixed">{t('plans.create.lateFeeKindFixed')}</option>
                <option value="percent">{t('plans.create.lateFeeKindPercent')}</option>
              </select>
            </FormField>
            {form.lateFeeKind && form.lateFeeKind !== 'none' && (
              <FormField id="p-lf-amount" label={t('plans.create.lateFeeAmount')}>
                <Input
                  id="p-lf-amount"
                  inputMode="decimal"
                  value={form.lateFeeAmount ?? ''}
                  onChange={(e) => setForm({ ...form, lateFeeAmount: e.target.value })}
                  placeholder={form.lateFeeKind === 'percent' ? '5.0000 = 5%' : `${form.currencyCode || ''} amount`}
                />
              </FormField>
            )}
          </>
        )}

        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PlanDetailDrawerProps {
  planId: string;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

function PlanDetailDrawer({ planId, onClose, onChanged }: PlanDetailDrawerProps): JSX.Element {
  const { t, i18n } = useTranslation('installments');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('installments.write');
  const canCollect = usePermission('installments.collect');
  const [plan, setPlan] = useState<InstallmentPlan | null>(null);
  const [schedule, setSchedule] = useState<InstallmentScheduleRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [payingRowId, setPayingRowId] = useState<string | null>(null);
  const money = (a: string, c: string | undefined): string =>
    c ? formatMoney(a, { locale: i18n.language, currency: c }) : a;

  const reload = async (): Promise<void> => {
    const p = await api().get<InstallmentPlan>(`/installments/plans/${planId}`);
    setPlan(p.data);
    const s = await api().get<{ items: InstallmentScheduleRow[] }>(`/installments/plans/${planId}/schedule`);
    setSchedule(s.data.items);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const onActivate = async (): Promise<void> => {
    setBusy(true);
    try {
      await api().post(`/installments/plans/${planId}/activate`);
      await reload();
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onCancelPlan = async (): Promise<void> => {
    if (!(await confirmDialog({ message: t('plans.confirmCancel'), tone: 'danger' }))) return;
    setBusy(true);
    try {
      await api().post(`/installments/plans/${planId}/cancel`);
      await reload();
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onWriteOffRow = async (rowId: string): Promise<void> => {
    const reason = window.prompt(t('plans.writeOff.reasonPrompt'), '');
    if (reason === null) return;
    setBusy(true);
    try {
      await api().post(`/installments/schedule/${rowId}/write-off`, {
        reason: reason.trim() ? reason.trim() : undefined,
      });
      await reload();
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        className="fixed inset-y-0 end-0 z-50 flex w-full max-w-2xl flex-col overflow-y-auto bg-bg shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold">
            {plan ? plan.planNumber : '…'}{' '}
            {plan && <Badge tone={STATUS_TONE[plan.status]}>{t(`plans.status.${plan.status}`)}</Badge>}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-fg-subtle hover:bg-bg-muted"
            aria-label={tc('actions.cancel')}
          >
            <span aria-hidden>×</span>
          </button>
        </header>

        <div className="flex flex-col gap-4 p-4">
          {plan && (
            <Card>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-fg-muted">{t('plans.create.principal')}</dt>
                <dd className="font-mono">{money(plan.principal, plan.currencyCode)}</dd>
                <dt className="text-fg-muted">{t('plans.create.markupRate')}</dt>
                <dd className="font-mono">{plan.markupRate}</dd>
                <dt className="text-fg-muted">{t('plans.columns.total')}</dt>
                <dd className="font-mono">{money(plan.total, plan.currencyCode)}</dd>
                <dt className="text-fg-muted">{t('plans.create.count')}</dt>
                <dd className="font-mono">{plan.installmentCount} × {plan.frequency}</dd>
                <dt className="text-fg-muted">{t('plans.create.firstDue')}</dt>
                <dd className="font-mono">{plan.firstDueDate}</dd>
              </dl>
              {canWrite && (
                <div className="mt-4 flex gap-2">
                  {plan.status === 'draft' && (
                    <Button onClick={onActivate} loading={busy}>{t('plans.activate')}</Button>
                  )}
                  {(plan.status === 'draft' || plan.status === 'active') && (
                    <Button variant="secondary" onClick={onCancelPlan} disabled={busy}>{t('plans.cancel')}</Button>
                  )}
                </div>
              )}
            </Card>
          )}

          {schedule.length === 0 ? (
            <EmptyState title={t('plans.scheduleEmpty')} />
          ) : (
            <Card className="overflow-x-auto p-0">
              <table className="w-full text-start text-sm">
                <thead className="bg-bg-muted text-fg-subtle">
                  <tr>
                    <th className="px-3 py-2 text-start">#</th>
                    <th className="px-3 py-2 text-start">{t('plans.schedule.dueDate')}</th>
                    <th className="px-3 py-2 text-end">{t('plans.schedule.amountDue')}</th>
                    <th className="px-3 py-2 text-end">{t('plans.schedule.amountPaid')}</th>
                    <th className="px-3 py-2 text-start">{t('plans.schedule.status')}</th>
                    <th className="px-3 py-2" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((row) => {
                    const outstanding = (Number(row.amountDue) - Number(row.amountPaid)).toFixed(getCurrencyDecimals(plan?.currencyCode) ?? 4);
                    const canPayThis = canCollect
                      && plan?.status === 'active'
                      && (row.status === 'pending' || row.status === 'partial' || row.status === 'overdue');
                    return (
                      <Fragment key={row.id}>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 font-mono text-xs">{row.installmentNo}</td>
                          <td className="px-3 py-2 font-mono text-xs">{row.dueDate}</td>
                          <td className="px-3 py-2 text-end font-mono text-xs">{money(row.amountDue, plan?.currencyCode)}</td>
                          <td className="px-3 py-2 text-end font-mono text-xs">{money(row.amountPaid, plan?.currencyCode)}</td>
                          <td className="px-3 py-2">
                            <Badge tone={SCHEDULE_TONE[row.status]}>{t(`plans.scheduleStatus.${row.status}`)}</Badge>
                          </td>
                          <td className="px-3 py-2 text-end">
                            {canPayThis && payingRowId !== row.id && (
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setPayingRowId(row.id)}>
                                  {t('plans.pay.button')}
                                </Button>
                                {canWrite && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => void onWriteOffRow(row.id)}
                                    disabled={busy}
                                  >
                                    {t('plans.writeOff.button')}
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        {payingRowId === row.id && (
                          <tr className="border-t border-border bg-bg-subtle">
                            <td className="p-3" colSpan={6}>
                              <RecordPaymentForm
                                scheduleId={row.id}
                                defaultAmount={outstanding}
                                currencyCode={plan?.currencyCode ?? 'USD'}
                                onCancel={() => setPayingRowId(null)}
                                onPaid={async () => {
                                  setPayingRowId(null);
                                  await reload();
                                  await onChanged();
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------

interface RecordPaymentFormProps {
  scheduleId: string;
  defaultAmount: string;
  currencyCode: string;
  onCancel: () => void;
  onPaid: () => void | Promise<void>;
}

function RecordPaymentForm({
  scheduleId, defaultAmount, currencyCode, onCancel, onPaid,
}: RecordPaymentFormProps): JSX.Element {
  const { t } = useTranslation('installments');
  const { t: tc } = useTranslation();
  const [amount, setAmount] = useState(defaultAmount);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post(`/installments/schedule/${scheduleId}/pay`, {
        amount, paymentDate, reference: reference || undefined,
      });
      await onPaid();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 gap-3 md:grid-cols-4 md:items-end" noValidate>
      <FormField id="pay-amount" label={`${t('plans.pay.amount')} (${currencyCode})`} required>
        <Input id="pay-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required autoFocus />
      </FormField>
      <FormField id="pay-date" label={t('plans.pay.date')} required>
        <Input id="pay-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
      </FormField>
      <FormField id="pay-ref" label={t('plans.pay.reference')}>
        <Input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="receipt #, txn id…" />
      </FormField>
      <div className="flex gap-2">
        <Button type="submit" loading={saving} size="sm">{t('plans.pay.submit')}</Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
      </div>
      {error && <p className="md:col-span-4 text-sm text-danger" role="alert">{error}</p>}
    </form>
  );
}
